import { useEffect, useState, type FunctionComponent } from "react";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Typography,
} from "antd";
import dayjs, { type Dayjs } from "dayjs";
import capitalize from "lodash.capitalize";
import { useTranslation } from "react-i18next";

import { productActions } from "@fitness-app/app-store";
import { RadixColor } from "@fitness-app/data-models/entities/CheckoutVariant";
import { ProductPriceAction, type ProductPrice } from "@fitness-app/data-models/entities/Product";
import { generateUniqId } from "@fitness-app/utils/src/helpers/generateUniqId";

import { useProductPricesOptions } from "~/modules/Products/hooks/useProductPricesOptions";
import DuplicatePriceModal from "~/modules/Products/Product/ProductPrices/DuplicatePriceModal";
import { useUpsellTargetOptions } from "~/modules/Products/hooks/useUpsellTargetOptions";
import { useAppDispatch } from "~/store/initializeStore";
import { colorThemeOptions } from "../ProductSettings/utils/theme-options";

interface OwnProps {
  price: ProductPrice;
  extended: boolean;
  productId: string;
  productPricesActions: null | { id: string; action: ProductPriceAction }[];
  canRemove: boolean;
}

type Props = OwnProps;

interface ConfigFormModel {
  showAsPromo: boolean;
  priceBeforeDiscount?: number;
  promoTagText?: string | null;
  promoTagColor?: RadixColor | null;
  showTimeLimit: boolean;
  priceTimeLimit?: Dayjs | null;
  showQuantityLimit: boolean;
  priceQuantityLimit?: number | null;
  showQuantityAvailable: boolean;
  showAdditionalInfo: boolean;
  priceAdditionalInfo?: string | null;
  setUpsell: boolean;
  upsellPrices?: string[] | null;
  onlyForRenewal?: boolean;
  forAccessProlongationNotRequirePayment?: boolean;
  allowManualPayment?: boolean;
  allowOverlapRenewalWithCurrentSubscription?: boolean;
  showAsVipPrice?: boolean;
  payoffUpsellEnabled?: boolean;
  payoffUpsellWindowDays?: number | null;
  payoffUpsellDiscountPct?: number | null;
  showRichCard?: boolean;
  benefitsTitle?: string | null;
  isRecommended?: boolean;
  recommendedLabel?: string | null;
  cornerBadgeLabel?: string | null;
  ctaLabel?: string | null;
  selectedCtaLabel?: string | null;
  benefits?: {
    title: string;
    description?: string | null;
    icon?: string | null;
  }[];
  notice?: string | null;
  setSuccessPageUpsell: boolean;
  upsellTargetProductId?: string | null;
  upsellTargetPriceId?: string | null;
  upsellHeadline?: string | null;
  upsellDescription?: string | null;
  upsellBenefitsTitle?: string | null;
  upsellBenefits?: {
    title: string;
    description?: string | null;
    icon?: string | null;
  }[];
  upsellPriceBeforeDiscount?: number | null;
  upsellBadgeLabel?: string | null;
  upsellTimerMinutes?: number | null;
  upsellAcceptCtaLabel?: string | null;
  upsellDeclineCtaLabel?: string | null;
}
const trimValue = (value?: string | null) => (value?.trim().length ? value.trim() : null);

const PriceConfig: FunctionComponent<Props> = ({ price, extended, productPricesActions, productId, canRemove }) => {
  const [showPromoModal, togglePromoModal] = useState(false);
  const [showDuplicateModal, toggleDuplicateModal] = useState(false);
  const [formController] = Form.useForm<ConfigFormModel>();
  const showAsPromo = Form.useWatch("showAsPromo", formController);
  const showTimeLimit = Form.useWatch("showTimeLimit", formController);
  const showQuantityLimit = Form.useWatch("showQuantityLimit", formController);
  const showQuantityAvailable = Form.useWatch("showQuantityAvailable", formController);
  const showAdditionalInfo = Form.useWatch("showAdditionalInfo", formController);
  const setUpsell = Form.useWatch("setUpsell", formController);
  const showRichCard = Form.useWatch("showRichCard", formController);
  const setSuccessPageUpsell = Form.useWatch("setSuccessPageUpsell", formController);
  const upsellTargetProductId = Form.useWatch("upsellTargetProductId", formController);
  const upsellTargetPriceId = Form.useWatch("upsellTargetPriceId", formController);
  const payoffUpsellEnabled = Form.useWatch("payoffUpsellEnabled", formController);
  const productPricesOptions = useProductPricesOptions(price.id);
  const { productOptions: upsellProductOptions, getPriceOptionsFor, eligibleProducts } = useUpsellTargetOptions();

  const [saving, toggleSavingFlag] = useState(false);
  const { t } = useTranslation("products");
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (showPromoModal) {
      const showAsPromo = Boolean(price.config?.showAsPromo);
      const showTimeLimit = Boolean(price.config?.activeUntil);
      const showQuantityLimit = Boolean(price.config?.quantityLimit);
      const showQuantityAvailable = Boolean(price.config?.showAvailable);
      const showAdditionalInfo = Boolean(price.config?.additionalInfo);
      const showRichCard = Boolean(price.config?.extra);
      formController?.setFieldsValue({
        showAsPromo,
        priceBeforeDiscount: price.config?.showAsPromo ? price.config.showAsPromo.priceBeforeDiscount / 100 : 0,
        showTimeLimit,
        priceTimeLimit: price.config?.activeUntil ? dayjs(price.config?.activeUntil) : null,
        showQuantityLimit,
        priceQuantityLimit: price.config?.quantityLimit ?? 100,
        showQuantityAvailable,
        showAdditionalInfo,
        priceAdditionalInfo: price.config?.additionalInfo,
        onlyForRenewal: price.config?.onlyForRenewal ?? false,
        forAccessProlongationNotRequirePayment: price.config?.forAccessProlongationNotRequirePayment ?? false,
        allowManualPayment: price.config?.allowManualPayment ?? false,
        allowOverlapRenewalWithCurrentSubscription: price.config?.allowOverlapRenewalWithCurrentSubscription ?? false,
        showAsVipPrice: price.config?.showAsVipPrice ?? false,
        payoffUpsellEnabled: Boolean(price.config?.payoffUpsell?.enabled),
        payoffUpsellWindowDays: price.config?.payoffUpsell?.windowDays ?? 30,
        payoffUpsellDiscountPct: price.config?.payoffUpsell?.discountPct ?? 10,
        promoTagText: price.config?.showAsPromo ? (price.config.showAsPromo.promoTagText ?? null) : null,
        promoTagColor: price.config?.showAsPromo ? (price.config.showAsPromo.promoTagColor ?? null) : null,
        showRichCard,
        benefitsTitle: price.config?.extra?.benefitsTitle ?? null,
        isRecommended: price.config?.extra?.isRecommended ?? false,
        recommendedLabel: price.config?.extra?.recommendedLabel ?? null,
        cornerBadgeLabel: price.config?.extra?.cornerBadgeLabel ?? null,
        ctaLabel: price.config?.extra?.ctaLabel ?? null,
        selectedCtaLabel: price.config?.extra?.selectedCtaLabel ?? null,
        benefits: price.config?.extra?.benefits ?? [],
        notice: price.config?.extra?.notice ?? null,
        setSuccessPageUpsell: Boolean(price.config?.successPageUpsell?.enabled),
        upsellTargetProductId: price.config?.successPageUpsell?.targetProductId ?? null,
        upsellTargetPriceId: price.config?.successPageUpsell?.targetPriceId ?? null,
        upsellHeadline: price.config?.successPageUpsell?.headline ?? null,
        upsellDescription: price.config?.successPageUpsell?.description ?? null,
        upsellBenefitsTitle: price.config?.successPageUpsell?.benefitsTitle ?? null,
        upsellBenefits: price.config?.successPageUpsell?.benefits ?? [],
        upsellPriceBeforeDiscount: price.config?.successPageUpsell?.priceBeforeDiscount
          ? price.config.successPageUpsell.priceBeforeDiscount / 100
          : null,
        upsellBadgeLabel: price.config?.successPageUpsell?.badgeLabel ?? null,
        upsellTimerMinutes: price.config?.successPageUpsell?.timerMinutes ?? null,
        upsellAcceptCtaLabel: price.config?.successPageUpsell?.acceptCtaLabel ?? null,
        upsellDeclineCtaLabel: price.config?.successPageUpsell?.declineCtaLabel ?? null,
      });
    } else {
      setTimeout(() => {
        formController.resetFields();
      }, 300);
    }
  }, [showPromoModal, price.id, price.config]);

  const onConfigFormSubmit = async (data: ConfigFormModel) => {
    toggleSavingFlag(true);
    const mapBenefits = (
      benefits?: {
        title: string;
        description?: string | null;
        icon?: string | null;
      }[],
    ) =>
      benefits
        ?.map((benefit) => ({
          title: benefit.title?.trim() ?? "",
          description: trimValue(benefit.description ?? null),
          icon: trimValue(benefit.icon ?? null),
        }))
        .filter((benefit) => benefit.title) ?? [];

    const benefits = mapBenefits(data.benefits);

    try {
      await dispatch(
        productActions.updateProductPriceConfig({
          priceId: price.id,
          productId,
          config: {
            additionalInfo: data.showAdditionalInfo ? data.priceAdditionalInfo : null,
            activeUntil: data.showTimeLimit ? (data.priceTimeLimit?.valueOf() ?? null) : null,
            quantityLimit: data.showQuantityLimit ? data.priceQuantityLimit : null,
            showAvailable: data.showQuantityAvailable ?? false,
            showAsPromo:
              data.showAsPromo && data.priceBeforeDiscount
                ? {
                    priceBeforeDiscount: data.priceBeforeDiscount * 100,
                    promoTagText: data.promoTagText ?? null,
                    promoTagColor: data.promoTagColor ?? null,
                  }
                : false,
            onlyForRenewal: data.onlyForRenewal ?? false,
            forAccessProlongationNotRequirePayment: data.forAccessProlongationNotRequirePayment ?? false,
            allowManualPayment: data.allowManualPayment ?? false,
            allowOverlapRenewalWithCurrentSubscription: data.allowOverlapRenewalWithCurrentSubscription ?? false,
            showAsVipPrice: data.showAsVipPrice ?? false,
            payoffUpsell:
              data.payoffUpsellEnabled && data.payoffUpsellWindowDays && data.payoffUpsellDiscountPct
                ? {
                    enabled: true,
                    windowDays: data.payoffUpsellWindowDays,
                    discountPct: data.payoffUpsellDiscountPct,
                  }
                : null,
            extra: data.showRichCard
              ? {
                  benefits,
                  benefitsTitle: trimValue(data.benefitsTitle ?? null),
                  isRecommended: data.isRecommended ?? false,
                  recommendedLabel: trimValue(data.recommendedLabel ?? null),
                  cornerBadgeLabel: trimValue(data.cornerBadgeLabel ?? null),
                  ctaLabel: trimValue(data.ctaLabel ?? null),
                  selectedCtaLabel: trimValue(data.selectedCtaLabel ?? null),
                  notice: trimValue(data.notice ?? null),
                }
              : null,
            successPageUpsell:
              data.setSuccessPageUpsell && data.upsellTargetProductId && data.upsellTargetPriceId
                ? {
                    enabled: true,
                    targetProductId: data.upsellTargetProductId,
                    targetPriceId: data.upsellTargetPriceId,
                    headline: trimValue(data.upsellHeadline ?? null),
                    description: trimValue(data.upsellDescription ?? null),
                    benefitsTitle: trimValue(data.upsellBenefitsTitle ?? null),
                    benefits: mapBenefits(data.upsellBenefits),
                    priceBeforeDiscount: data.upsellPriceBeforeDiscount
                      ? Math.round(data.upsellPriceBeforeDiscount * 100)
                      : null,
                    badgeLabel: trimValue(data.upsellBadgeLabel ?? null),
                    timerMinutes: data.upsellTimerMinutes ?? null,
                    acceptCtaLabel: trimValue(data.upsellAcceptCtaLabel ?? null),
                    declineCtaLabel: trimValue(data.upsellDeclineCtaLabel ?? null),
                  }
                : null,
          },
        }),
      ).unwrap();
      toggleSavingFlag(false);
      togglePromoModal(false);
    } catch {
      toggleSavingFlag(false);
      void message.error("common:messages.error.default");
    }

    await dispatch(
      productActions.updateProductPrices({
        payload: {
          prices: [
            {
              productId,
              priceId: price.id,
              connectedUpsells: data.upsellPrices ?? null,
              action: ProductPriceAction.SetUpsell,
            },
          ],
        },
        onSuccess: () => {
          toggleSavingFlag(false);
          togglePromoModal(false);
        },
        onFailure: () => {
          toggleSavingFlag(false);
        },
      }),
    );

    if (data.upsellPrices?.length) {
      data.upsellPrices.forEach(
        async (upsellPrice) =>
          await dispatch(
            productActions.updateProductPrices({
              payload: {
                prices: [
                  {
                    productId,
                    priceId: upsellPrice,
                    hidden: true,
                    checkoutId: generateUniqId(),
                    action: ProductPriceAction.ToggleVisibility,
                  },
                ],
              },
              onSuccess: () => {
                toggleSavingFlag(false);
                togglePromoModal(false);
              },
              onFailure: () => {
                toggleSavingFlag(false);
              },
            }),
          ),
      );
    }
  };

  const currentlyProcessed = productPricesActions ? productPricesActions.find((item) => item.id === price.id) : null;

  const upsellTargetPrice = eligibleProducts
    .find((product) => product.id === upsellTargetProductId)
    ?.prices.find((targetPrice) => targetPrice.id === upsellTargetPriceId);

  return (
    <>
      <Space>
        {extended && (
          <Button type="link" onClick={() => togglePromoModal(true)}>
            {t("common:button.configure")}
          </Button>
        )}
        {extended && (
          <Button type="link" onClick={() => toggleDuplicateModal(true)}>
            {t("prices.duplicate")}
          </Button>
        )}
        {price.clients ? (
          <Popconfirm
            overlayStyle={{ maxWidth: 400 }}
            placement="leftTop"
            disabled={!canRemove}
            title={t("mailing.archivePriceWarning")}
            onConfirm={() =>
              dispatch(
                productActions.updateProductPrices({
                  onFailure: () => {
                    void message.error(t("errorWhenUpdatingPrice"));
                  },
                  payload: {
                    prices: [
                      {
                        productId,
                        priceId: price.id,
                        action: ProductPriceAction.Archive,
                      },
                    ],
                  },
                }),
              )
            }
          >
            <Button
              type="link"
              danger
              disabled={!canRemove}
              loading={currentlyProcessed ? currentlyProcessed.action === ProductPriceAction.Archive : false}
            >
              {t("common:button.archive")}
            </Button>
          </Popconfirm>
        ) : (
          <Popconfirm
            overlayStyle={{ maxWidth: 400 }}
            placement="leftTop"
            disabled={!canRemove}
            title={t("mailing.deletePriceWarning")}
            onConfirm={() =>
              dispatch(
                productActions.updateProductPrices({
                  onFailure: () => {
                    void message.error(t("errorWhenUpdatingPrice"));
                  },
                  payload: {
                    prices: [
                      {
                        productId,
                        priceId: price.id,
                        action: ProductPriceAction.Remove,
                      },
                    ],
                  },
                }),
              )
            }
          >
            <Button
              type="link"
              danger
              disabled={!canRemove}
              loading={currentlyProcessed ? currentlyProcessed.action === ProductPriceAction.Remove : false}
            >
              {t("common:button.delete")}
            </Button>
          </Popconfirm>
        )}
      </Space>
      <Modal
        visible={showPromoModal}
        width={600}
        onCancel={() => {
          togglePromoModal(false);
        }}
        okText={t("common:button.save")}
        title={t("prices.modalTitle")}
        onOk={() => formController?.submit()}
        okButtonProps={{
          loading: saving,
          disabled: saving,
        }}
      >
        <Form<ConfigFormModel>
          wrapperCol={{ span: 16 }}
          onFinish={onConfigFormSubmit}
          form={formController}
          initialValues={{
            showAsPromo: false,
            showAsVipPrice: false,
            priceBeforeDiscount: 0,
            showTimeLimit: false,
            priceTimeLimit: null,
            showQuantityLimit: false,
            priceQuantityLimit: 100,
            showQuantityAvaliable: false,
            promoTagColor: RadixColor.Red,
            showRichCard: false,
            isRecommended: false,
            benefits: [],
            setSuccessPageUpsell: false,
            upsellBenefits: [],
          }}
        >
          <Form.Item label={t("prices.showAsPromo")} name="showAsPromo" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item label={t("prices.showAsVipPrice")} name="showAsVipPrice" valuePropName="checked">
            <Switch />
          </Form.Item>
          {showAsPromo && (
            <>
              <Form.Item
                label={t("prices.priceBefore")}
                name="priceBeforeDiscount"
                tooltip={t("prices.willBeShowsStrikethrough")}
                rules={[
                  {
                    required: showAsPromo,
                    type: "number",
                    message: t<string>("common:validationErrors.fieldIsRequired"),
                  },
                  {
                    validator: (_, value) =>
                      value > (price.unit_amount ?? 0) / 100
                        ? Promise.resolve()
                        : Promise.reject(new Error(t<string>("prices.cannotBeLessThanCurrent"))),
                  },
                ]}
              >
                <InputNumber
                  min={0}
                  max={100000}
                  precision={2}
                  addonAfter={price.currency.toUpperCase()}
                  decimalSeparator={price.currency === "pln" ? "," : "."}
                />
              </Form.Item>

              <Form.Item name={["promoTagColor"]} label={t<string>("pageConfig.promoTagColor")}>
                <Select style={{ width: 180 }}>
                  {colorThemeOptions().map((item) => (
                    <Select.Option key={item.value} value={item.value}>
                      {t<string>(`${capitalize(item.value)}`)}
                      {item.color && (
                        <div
                          style={{
                            position: "absolute",
                            right: "1.2rem",
                            top: ".35rem",
                            width: "1.2rem",
                            height: "1.2rem",
                            backgroundColor: item.color,
                            borderRadius: ".6rem",
                          }}
                        />
                      )}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item label={t("pageConfig.promoTagText")} name="promoTagText">
                <Input
                  count={{
                    max: 30,
                    show: true,
                  }}
                />
              </Form.Item>
            </>
          )}

          <Form.Item label={t("prices.showTimeLimit")} name="showTimeLimit" valuePropName="checked">
            <Switch />
          </Form.Item>
          {showTimeLimit && (
            <Form.Item
              label={t("prices.priceTime")}
              name="priceTimeLimit"
              tooltip={t("prices.willBeShowsTimer")}
              rules={[
                {
                  required: showTimeLimit,
                  message: t<string>("common:validationErrors.fieldIsRequired"),
                },
                {
                  validator: (_, value) =>
                    value > Date.now()
                      ? Promise.resolve()
                      : Promise.reject(new Error(t<string>("prices.cannotBeEarlierThanNow"))),
                },
              ]}
            >
              <DatePicker
                showTime
                format="YYYY-MM-DD | HH:mm"
                disabledDate={(current) => current && current.valueOf() < Date.now() - 60 * 60 * 24}
              />
            </Form.Item>
          )}

          <Form.Item label={t("prices.showQuantityLimit")} name="showQuantityLimit" valuePropName="checked">
            <Switch />
          </Form.Item>
          {showQuantityLimit && (
            <>
              <Form.Item
                label={t("prices.priceQuantity")}
                name="priceQuantityLimit"
                tooltip={t("prices.willBeShowsQuantity")}
                rules={[
                  {
                    required: showQuantityLimit,
                    type: "number",
                    message: t<string>("common:validationErrors.fieldIsRequired"),
                  },
                  {
                    validator: (_, value) =>
                      value >= price.clients
                        ? Promise.resolve()
                        : Promise.reject(new Error(t<string>("prices.cannotBeLessThanSold"))),
                  },
                ]}
              >
                <InputNumber min={0} max={100000} />
              </Form.Item>
              <Form.Item label={t("prices.showQuantityAvailable")} name="showQuantityAvailable" valuePropName="checked">
                <Switch />
              </Form.Item>
              {showQuantityAvailable && (
                <div className="-mt-4 mb-4">
                  <Typography.Text type="secondary">{t("prices.quantityAvailableExample")}</Typography.Text>
                </div>
              )}
            </>
          )}
          <Form.Item label={t("prices.showAdditionalInfo")} name="showAdditionalInfo" valuePropName="checked">
            <Switch />
          </Form.Item>
          {showAdditionalInfo && (
            <>
              <Form.Item
                label={t("prices.additionalInfo")}
                name="priceAdditionalInfo"
                tooltip={t("prices.willBeShowsInfo")}
                rules={[
                  {
                    required: showAdditionalInfo,
                    message: t<string>("common:validationErrors.fieldIsRequired"),
                  },
                ]}
              >
                <Input style={{ maxWidth: 280 }} />
              </Form.Item>
              <div className="-mt-4 mb-4">
                <Typography.Text type="secondary">{t("prices.additionalInfoExample")}</Typography.Text>
              </div>
            </>
          )}
          <Form.Item label={t("prices.showRichCard")} name="showRichCard" valuePropName="checked">
            <Switch />
          </Form.Item>
          {showRichCard && (
            <>
              <Form.Item label={t("prices.benefitsTitle")} name="benefitsTitle">
                <Input />
              </Form.Item>
              <Form.Item label={t("prices.isRecommended")} name="isRecommended" valuePropName="checked">
                <Switch />
              </Form.Item>
              <Form.Item label={t("prices.recommendedLabel")} name="recommendedLabel">
                <Input />
              </Form.Item>
              <Form.Item
                label={t("prices.cornerBadgeLabel")}
                name="cornerBadgeLabel"
                tooltip={t("prices.cornerBadgeLabelTooltip")}
              >
                <Input
                  placeholder={t<string>("prices.cornerBadgeLabelPlaceholder")}
                  count={{
                    max: 30,
                    show: true,
                  }}
                />
              </Form.Item>
              <Form.Item label={t("prices.ctaLabel")} name="ctaLabel">
                <Input />
              </Form.Item>
              <Form.Item label={t("prices.selectedCtaLabel")} name="selectedCtaLabel">
                <Input />
              </Form.Item>
              <Form.Item label={t("prices.notice")} name="notice">
                <Input.TextArea rows={2} />
              </Form.Item>
              <Form.List name="benefits">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map((field) => (
                      <div
                        key={field.key}
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 12,
                          alignItems: "baseline",
                        }}
                      >
                        <Form.Item
                          {...field}
                          label={t("prices.benefitTitle")}
                          name={[field.name, "title"]}
                          rules={[
                            {
                              required: true,
                              message: t<string>("common:validationErrors.fieldIsRequired"),
                            },
                          ]}
                          style={{ flex: 1, minWidth: 200 }}
                        >
                          <Input />
                        </Form.Item>
                        <Form.Item
                          {...field}
                          label={t("prices.benefitDescription")}
                          name={[field.name, "description"]}
                          style={{ flex: 1, minWidth: 220 }}
                        >
                          <Input />
                        </Form.Item>
                        <Form.Item
                          {...field}
                          label={t("prices.benefitIcon")}
                          name={[field.name, "icon"]}
                          tooltip={t("prices.benefitIconTooltip")}
                          style={{ flex: 1, minWidth: 180 }}
                        >
                          <Input placeholder={t<string>("prices.benefitIconPlaceholder")} />
                        </Form.Item>
                        <MinusCircleOutlined onClick={() => remove(field.name)} className="text-red-500" />
                      </div>
                    ))}
                    <Form.Item label=" " colon={false}>
                      <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                        {t("prices.addBenefit")}
                      </Button>
                    </Form.Item>
                  </>
                )}
              </Form.List>
            </>
          )}
          <Form.Item label={t<string>("prices.setUpsell")} name="setUpsell" valuePropName="checked">
            <Switch />
          </Form.Item>
          {setUpsell && (
            <Form.Item
              name="upsellPrices"
              label={t<string>("prices.upsellPrices")}
              tooltip={t<string>("prices.upsellInfo")}
              rules={[
                {
                  required: setUpsell,
                  message: t<string>("common:validationErrors.fieldIsRequired"),
                },
              ]}
            >
              <Select mode="multiple" showSearch optionFilterProp="label" options={productPricesOptions} />
            </Form.Item>
          )}

          <Form.Item
            label={t<string>("prices.successPageUpsell.enable")}
            tooltip={t<string>("prices.successPageUpsell.enableTooltip")}
            name="setSuccessPageUpsell"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
          {setSuccessPageUpsell && (
            <>
              {!upsellProductOptions.length && (
                <div className="-mt-4 mb-4">
                  <Typography.Text type="warning">{t("prices.successPageUpsell.noEligibleProducts")}</Typography.Text>
                </div>
              )}
              <Form.Item
                label={t<string>("prices.successPageUpsell.targetProduct")}
                name="upsellTargetProductId"
                rules={[
                  {
                    required: setSuccessPageUpsell,
                    message: t<string>("common:validationErrors.fieldIsRequired"),
                  },
                ]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={upsellProductOptions}
                  onChange={() => formController.setFieldsValue({ upsellTargetPriceId: null })}
                />
              </Form.Item>
              <Form.Item
                label={t<string>("prices.successPageUpsell.targetPrice")}
                name="upsellTargetPriceId"
                rules={[
                  {
                    required: setSuccessPageUpsell,
                    message: t<string>("common:validationErrors.fieldIsRequired"),
                  },
                ]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={getPriceOptionsFor(upsellTargetProductId)}
                  disabled={!upsellTargetProductId}
                />
              </Form.Item>
              <Form.Item label={t<string>("prices.successPageUpsell.headline")} name="upsellHeadline">
                <Input />
              </Form.Item>
              <Form.Item label={t<string>("prices.successPageUpsell.description")} name="upsellDescription">
                <Input.TextArea rows={3} />
              </Form.Item>
              <Form.Item
                label={t<string>("prices.successPageUpsell.priceBeforeDiscount")}
                name="upsellPriceBeforeDiscount"
                tooltip={t<string>("prices.successPageUpsell.priceBeforeDiscountTooltip")}
                rules={[
                  {
                    validator: (_, value) =>
                      !value || !upsellTargetPrice?.unit_amount || value > upsellTargetPrice.unit_amount / 100
                        ? Promise.resolve()
                        : Promise.reject(new Error(t<string>("prices.cannotBeLessThanCurrent"))),
                  },
                ]}
              >
                <InputNumber
                  min={0}
                  max={100000}
                  precision={2}
                  addonAfter={(upsellTargetPrice?.currency ?? price.currency).toUpperCase()}
                  decimalSeparator={(upsellTargetPrice?.currency ?? price.currency) === "pln" ? "," : "."}
                />
              </Form.Item>
              <Form.Item label={t<string>("prices.successPageUpsell.badgeLabel")} name="upsellBadgeLabel">
                <Input
                  count={{
                    max: 30,
                    show: true,
                  }}
                />
              </Form.Item>
              <Form.Item
                label={t<string>("prices.successPageUpsell.timerMinutes")}
                name="upsellTimerMinutes"
                tooltip={t<string>("prices.successPageUpsell.timerMinutesTooltip")}
              >
                <InputNumber min={1} max={1440} precision={0} />
              </Form.Item>
              <Form.Item label={t<string>("prices.successPageUpsell.acceptCtaLabel")} name="upsellAcceptCtaLabel">
                <Input />
              </Form.Item>
              <Form.Item label={t<string>("prices.successPageUpsell.declineCtaLabel")} name="upsellDeclineCtaLabel">
                <Input />
              </Form.Item>
              <Form.Item label={t<string>("prices.successPageUpsell.benefitsTitle")} name="upsellBenefitsTitle">
                <Input />
              </Form.Item>
              <Form.List name="upsellBenefits">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map((field) => (
                      <div
                        key={field.key}
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 12,
                          alignItems: "baseline",
                        }}
                      >
                        <Form.Item
                          {...field}
                          label={t("prices.benefitTitle")}
                          name={[field.name, "title"]}
                          rules={[
                            {
                              required: true,
                              message: t<string>("common:validationErrors.fieldIsRequired"),
                            },
                          ]}
                          style={{ flex: 1, minWidth: 200 }}
                        >
                          <Input />
                        </Form.Item>
                        <Form.Item
                          {...field}
                          label={t("prices.benefitDescription")}
                          name={[field.name, "description"]}
                          style={{ flex: 1, minWidth: 220 }}
                        >
                          <Input />
                        </Form.Item>
                        <Form.Item
                          {...field}
                          label={t("prices.benefitIcon")}
                          name={[field.name, "icon"]}
                          tooltip={t("prices.benefitIconTooltip")}
                          style={{ flex: 1, minWidth: 180 }}
                        >
                          <Input placeholder={t<string>("prices.benefitIconPlaceholder")} />
                        </Form.Item>
                        <MinusCircleOutlined onClick={() => remove(field.name)} className="text-red-500" />
                      </div>
                    ))}
                    <Form.Item label=" " colon={false}>
                      <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                        {t("prices.addBenefit")}
                      </Button>
                    </Form.Item>
                  </>
                )}
              </Form.List>
            </>
          )}

          <Form.Item
            tooltip={t<string>("prices.onlyForRenewalTooltip")}
            label={t<string>("prices.onlyForRenewal")}
            name="onlyForRenewal"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          {price.installments && (
            <>
              <Form.Item
                tooltip={t<string>("prices.forAccessProlongationNotRequirePaymentTooltip")}
                label={t<string>("prices.forAccessProlongationNotRequirePayment")}
                name="forAccessProlongationNotRequirePayment"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <Form.Item
                tooltip={t<string>("prices.allowOverlapRenewalWithCurrentSubscriptionTooltip")}
                label={t<string>("prices.allowOverlapRenewalWithCurrentSubscription")}
                name="allowOverlapRenewalWithCurrentSubscription"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <Form.Item
                tooltip={t<string>("prices.payoffUpsellTooltip")}
                label={t<string>("prices.payoffUpsell")}
                name="payoffUpsellEnabled"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
              {payoffUpsellEnabled && (
                <>
                  <Form.Item
                    label={t("prices.payoffUpsellWindowDays")}
                    name="payoffUpsellWindowDays"
                    tooltip={t("prices.payoffUpsellWindowDaysTooltip")}
                    rules={[
                      {
                        required: payoffUpsellEnabled,
                        type: "number",
                        min: 1,
                        max: 365,
                        message: t<string>("common:validationErrors.fieldIsRequired"),
                      },
                    ]}
                  >
                    <InputNumber min={1} max={365} precision={0} />
                  </Form.Item>
                  <Form.Item
                    label={t("prices.payoffUpsellDiscountPct")}
                    name="payoffUpsellDiscountPct"
                    tooltip={t("prices.payoffUpsellDiscountPctTooltip")}
                    rules={[
                      {
                        required: payoffUpsellEnabled,
                        type: "number",
                        min: 1,
                        max: 90,
                        message: t<string>("common:validationErrors.fieldIsRequired"),
                      },
                    ]}
                  >
                    <InputNumber min={1} max={90} precision={0} addonAfter="%" />
                  </Form.Item>
                </>
              )}
            </>
          )}

          <Form.Item
            label={t("prices.allowManualPayment")}
            tooltip={t("prices.allowManualPaymentTooltip")}
            name="allowManualPayment"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
      <DuplicatePriceModal
        price={price}
        productId={productId}
        open={showDuplicateModal}
        onClose={() => toggleDuplicateModal(false)}
      />
    </>
  );
};

export default PriceConfig;
